/**
 * Schools-mode key figures, shared by the page strip and the School modal.
 *
 * Port of LAE `features/productivity/shared/heroSchools.js` `buildSchoolHero`: the same 17 tiles, in the
 * same order, with the same labels, delta and sparkline rules, and the same `single` variant for the modal
 * (Total Leads, Leads Contacted and Leads Worked Upon first, with their share of the school's leads).
 *
 * Deliberate differences from LAE:
 * - no tile titles (definitions belong in the guide, not a hover);
 * - no click handlers (`onOpenChanges` / drill-downs are not ported) and no rubric;
 * - the three per-call averages (Avg Call Time, and its two variants) use the short duration format
 *   (minutes and seconds) instead of rounded minutes, matching the table's cells for the same keys.
 */
import type { KpiItem } from './KpiStrip';
import { fmtDay, fmtNum, fmtShortDuration, pctChange, withShare } from './lae-format';
import { formatMetric, metricLabel } from './lae-metrics';

/** 17 tiles sit in rows of 4 from 1024px up. */
export const SCHOOL_HERO_COLS = { lg: 4, xl: 4 } as const;

/** Hero labels that differ from the metric dictionary. */
export const SCHOOL_HERO_LABELS: Readonly<Record<string, string>> = {
  active_schools: 'Active Schools',
  allocated: 'Total Leads',
  avg_leads_per_school: 'Avg Leads per School',
  covered: 'Leads Contacted',
  coverage_pct: '% Leads Contacted',
};

/** Fallback labels for keys the metric dictionary may lack (LAE `SCHOOL_FALLBACK_LABELS`). */
const FALLBACK_LABELS: Readonly<Record<string, string>> = {
  counsellors_active: 'Active Counsellors',
  counselling_completed: 'Marked as Interested in 2027 Intakes',
  avg_talk_seconds: 'Avg Call Time',
  ccm_calls: 'Answered Calls - Interested in 2027 Intakes',
  ccm_talk_seconds: 'Total Call Time - Interested in 2027 Intakes',
  avg_ccm_talk_seconds: 'Avg Call Time - Interested in 2027 Intakes',
  cc_talk_seconds: 'Talk Time - Already Interested in 2027 Intakes',
  avg_cc_talk_seconds: 'Avg Talk Time - Already Interested in 2027 Intakes',
  matriculation: 'Reached Deposit / Visa',
};

/** Per-call averages as [talk seconds, answered calls], for totals or series points that lack the average. */
export const PER_CALL = {
  avg_talk_seconds: ['connected_seconds', 'connected'],
  avg_ccm_talk_seconds: ['ccm_talk_seconds', 'ccm_calls'],
  avg_adv_talk_seconds: ['adv_talk_seconds', 'adv_calls'],
} as const satisfies Record<string, readonly [string, string]>;

type PerCallKey = keyof typeof PER_CALL;

/** `den ? round(num / den × scale) / scale : null`: whole seconds by default. */
export const ratio = (num: number, den: number, scale = 1): number | null =>
  den ? Math.round((num / den) * scale) / scale : null;

interface ActivityRow {
  calls?: number | null;
  worked?: number | null;
}

/** Schools where anyone made a call or worked on a lead in the window. */
export const activeCount = (rows: readonly ActivityRow[] | null | undefined): number =>
  (rows ?? []).filter((r) => (r.calls ?? 0) > 0 || (r.worked ?? 0) > 0).length;

/** Any metric-shaped object (totals, a school row, a group subtotal, a series point). */
type MetricSource = object;
interface SeriesSource {
  bucket: string;
}

export interface BuildSchoolHeroOptions {
  totals: MetricSource | null | undefined;
  prev?: MetricSource | null;
  series?: readonly SeriesSource[] | null;
  schools?: readonly ActivityRow[] | null;
  prevSchools?: readonly ActivityRow[] | null;
  loading?: boolean;
  granularity?: 'day' | 'week';
  /** School modal: one school, so no school count or per-school average. */
  single?: boolean;
}

const SHORT_DURATION_KEYS: ReadonlySet<string> = new Set<string>(Object.keys(PER_CALL));

const labelOf = (key: string): string => {
  const known = metricLabel(key);
  if (known && known !== key) return known;
  return FALLBACK_LABELS[key] ?? known ?? key;
};

const fieldOf = (src: MetricSource, key: string): unknown => (src as Record<string, unknown>)[key];

const numOf = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

const perCallOf = (src: MetricSource, key: PerCallKey): number | null => {
  const [secs, calls] = PER_CALL[key];
  return ratio(numOf(fieldOf(src, secs)), numOf(fieldOf(src, calls)));
};

const valueOf = (src: MetricSource | null | undefined, key: string): number | null => {
  if (!src) return null;
  const v = fieldOf(src, key);
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return key in PER_CALL ? perCallOf(src, key as PerCallKey) : null;
};

const formatTile = (key: string, v: number | null | undefined): string =>
  SHORT_DURATION_KEYS.has(key) ? fmtShortDuration(v) : formatMetric(key, v);

type SparkFn = (s: SeriesSource) => number | null;

/** Ordered by importance: reach of the schools, lead coverage, outcomes, then calling effort and follow-up. */
export function buildSchoolHero({
  totals,
  prev = null,
  series = [],
  schools = [],
  prevSchools = null,
  loading = false,
  granularity,
  single = false,
}: BuildSchoolHeroOptions): KpiItem[] {
  const tot: MetricSource = totals ?? {};
  const pts = series ?? [];
  const rows = schools ?? [];
  const sparkLabels = pts.map((s) => (granularity === 'week' ? `Week of ${fmtDay(s.bucket)}` : fmtDay(s.bucket)));
  // Some counts reach series points later than totals; no sparkline until they do.
  const hasSeries = (k: string) => pts.some((s) => fieldOf(s, k) != null);

  // `spark`: a series key, or a function of one series point for derived figures.
  const item = (key: string, spark?: string | SparkFn): KpiItem => {
    const sparkFn: SparkFn | null =
      typeof spark === 'function' ? spark : spark ? (s) => numOf(fieldOf(s, spark)) : null;
    const cur = valueOf(tot, key);
    const out: KpiItem = {
      key,
      label: SCHOOL_HERO_LABELS[key] ?? labelOf(key),
      value: formatTile(key, cur),
      delta: prev ? pctChange(cur, valueOf(prev, key)) : null,
    };
    if (sparkFn) {
      out.spark = pts.map(sparkFn);
      out.sparkLabels = sparkLabels;
      out.sparkFormat = (v: number | null) => formatTile(key, v);
    }
    return out;
  };
  const perCall = (key: PerCallKey): SparkFn => (s) => perCallOf(s, key);
  const ifSeries = (k: string, spark: string | SparkFn = k) => (hasSeries(k) ? spark : undefined);

  const active = activeCount(rows);
  const perSchool = (src: MetricSource | null | undefined, n: number) =>
    src && n ? Math.round(numOf(fieldOf(src, 'allocated')) / n) : null;
  const avgLeads = perSchool(tot, rows.length);
  const prevAvgLeads = prevSchools ? perSchool(prev, prevSchools.length) : null;
  const connected = valueOf(tot, 'connected');

  const tiles: KpiItem[] = [
    {
      key: 'active_schools',
      label: SCHOOL_HERO_LABELS.active_schools ?? 'Active Schools',
      value: loading ? '…' : `${fmtNum(active)} / ${fmtNum(rows.length)}`,
      delta: prevSchools ? pctChange(active, activeCount(prevSchools)) : null,
    },
    item('allocated'),
    {
      key: 'avg_leads_per_school',
      label: SCHOOL_HERO_LABELS.avg_leads_per_school ?? 'Avg Leads per School',
      value: avgLeads == null ? '—' : fmtNum(avgLeads),
      delta: prevAvgLeads == null ? null : pctChange(avgLeads, prevAvgLeads),
    },
    item('covered'),
    item('coverage_pct'),
    item('counselling_completed', ifSeries('counselling_completed')),
    item('advanced', 'advanced'),
    item('calls', 'calls'),
    // Answered calls with the answer rate beside it; the delta and sparkline follow the answered count.
    {
      ...item('connected', 'connected'),
      key: 'connect_rate',
      label: 'Total Calls Answered',
      value:
        connected == null ? '—' : `${fmtNum(connected)} (${formatMetric('connect_rate', valueOf(tot, 'connect_rate'))})`,
    },
    item('dial_seconds', ifSeries('dial_seconds')),
    item('call_seconds', 'call_seconds'),
    item('avg_talk_seconds', perCall('avg_talk_seconds')),
    item('avg_ccm_talk_seconds', ifSeries('ccm_calls', perCall('avg_ccm_talk_seconds'))),
    item('avg_adv_talk_seconds', perCall('avg_adv_talk_seconds')),
    item('whatsapp_prepared', ifSeries('whatsapp_prepared')),
    item('emails_sent', ifSeries('emails_sent')),
    item('tasks_done', ifSeries('tasks_done')),
  ];
  if (!single) return tiles;

  // One school: reach tiles carry their share of the school's leads.
  const leads = numOf(fieldOf(tot, 'allocated'));
  const drop = new Set(['active_schools', 'allocated', 'avg_leads_per_school', 'covered', 'coverage_pct']);
  return [
    item('allocated'),
    { ...item('covered'), value: withShare(valueOf(tot, 'covered'), leads) },
    { ...item('worked'), label: 'Leads Worked Upon', value: withShare(valueOf(tot, 'worked'), leads) },
    ...tiles.filter((t) => !drop.has(t.key)),
  ];
}
